import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const CurrencySettings = () => {
  const [currencies, setCurrencies] = useState([
    {
      id: 1,
      code: 'USD',
      name: 'US Dollar',
      symbol: '$',
      exchangeRate: 1.0,
      isBase: true,
      isActive: true,
      lastUpdated: '2025-01-29'
    },
    {
      id: 2,
      code: 'EUR',
      name: 'Euro',
      symbol: '€',
      exchangeRate: 0.85,
      isBase: false,
      isActive: true,
      lastUpdated: '2025-01-29'
    },
    {
      id: 3,
      code: 'GBP',
      name: 'British Pound',
      symbol: '£',
      exchangeRate: 0.73,
      isBase: false,
      isActive: true,
      lastUpdated: '2025-01-29'
    },
    {
      id: 4,
      code: 'JPY',
      name: 'Japanese Yen',
      symbol: '¥',
      exchangeRate: 110.0,
      isBase: false,
      isActive: false,
      lastUpdated: '2025-01-20'
    }
  ]);

  const [currencySettings, setCurrencySettings] = useState({
    autoUpdateRates: true,
    updateFrequency: 'daily',
    rateProvider: 'ecb',
    showCurrencySymbol: true,
    currencyPosition: 'before',
    negativeFormat: 'parentheses',
    decimalPlaces: 2
  });

  const [showAddModal, setShowAddModal] = useState(false);
  const [newCurrency, setNewCurrency] = useState({
    code: '',
    name: '',
    symbol: '',
    exchangeRate: 1.0
  });

  const availableCurrencies = [
    { code: 'CAD', name: 'Canadian Dollar', symbol: 'C$' },
    { code: 'AUD', name: 'Australian Dollar', symbol: 'A$' },
    { code: 'CHF', name: 'Swiss Franc', symbol: 'CHF' },
    { code: 'CNY', name: 'Chinese Yuan', symbol: '¥' },
    { code: 'INR', name: 'Indian Rupee', symbol: '₹' },
    { code: 'SGD', name: 'Singapore Dollar', symbol: 'S$' },
    { code: 'HKD', name: 'Hong Kong Dollar', symbol: 'HK$' },
    { code: 'SEK', name: 'Swedish Krona', symbol: 'kr' },
    { code: 'NOK', name: 'Norwegian Krone', symbol: 'kr' },
    { code: 'DKK', name: 'Danish Krone', symbol: 'kr' }
  ];

  const rateProviders = [
    { value: 'ecb', label: 'European Central Bank' },
    { value: 'fed', label: 'Federal Reserve' },
    { value: 'boe', label: 'Bank of England' },
    { value: 'manual', label: 'Manual Entry Only' }
  ];

  const updateFrequencies = [
    { value: 'realtime', label: 'Real-time' },
    { value: 'hourly', label: 'Hourly' },
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'manual', label: 'Manual Only' }
  ];

  const negativeFormats = [
    { value: 'minus', label: '-$100.00' },
    { value: 'parentheses', label: '($100.00)' },
    { value: 'red', label: '$100.00 (red)' }
  ];

  const handleAddCurrency = () => {
    const currency = {
      ...newCurrency,
      id: Math.max(...currencies?.map(c => c?.id), 0) + 1,
      isBase: false,
      isActive: true,
      lastUpdated: new Date()?.toISOString()?.split('T')?.[0]
    };
    setCurrencies(prev => [...prev, currency]);
    setNewCurrency({ code: '', name: '', symbol: '', exchangeRate: 1.0 });
    setShowAddModal(false);
  };

  const handleDeleteCurrency = (currencyId) => {
    setCurrencies(prev => prev?.filter(currency => currency?.id !== currencyId));
  };

  const toggleCurrencyStatus = (currencyId) => {
    setCurrencies(prev => 
      prev?.map(currency => 
        currency?.id === currencyId 
          ? { ...currency, isActive: !currency?.isActive }
          : currency
      )
    );
  };

  const setBaseCurrency = (currencyId) => {
    setCurrencies(prev => 
      prev?.map(currency => ({
        ...currency,
        isBase: currency?.id === currencyId,
        exchangeRate: currency?.id === currencyId ? 1.0 : currency?.exchangeRate
      }))
    );
  };

  const updateExchangeRate = (currencyId, newRate) => {
    setCurrencies(prev => 
      prev?.map(currency => 
        currency?.id === currencyId 
          ? { 
              ...currency, 
              exchangeRate: parseFloat(newRate),
              lastUpdated: new Date()?.toISOString()?.split('T')?.[0]
            }
          : currency
      )
    );
  };

  const refreshAllRates = () => {
    console.log('Refreshing all exchange rates...');
    // TODO: Implement API call to refresh rates
    setCurrencies(prev => 
      prev?.map(currency => ({
        ...currency,
        lastUpdated: new Date()?.toISOString()?.split('T')?.[0]
      }))
    );
  };

  const handleSettingChange = (key, value) => {
    setCurrencySettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-foreground">Currency Settings</h2>
          <p className="text-muted-foreground">Manage currencies, exchange rates, and formatting preferences</p>
        </div>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={refreshAllRates} iconName="RefreshCw">
            Refresh Rates
          </Button>
          <Button onClick={() => setShowAddModal(true)} iconName="Plus">
            Add Currency
          </Button>
        </div>
      </div>
      {/* Currency Configuration */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Currency Configuration</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <label className="flex items-center justify-between cursor-pointer">
            <div>
              <span className="text-sm font-medium text-foreground">Auto-update Exchange Rates</span>
              <p className="text-xs text-muted-foreground">Automatically fetch latest rates</p>
            </div>
            <input
              type="checkbox"
              checked={currencySettings?.autoUpdateRates}
              onChange={(e) => handleSettingChange('autoUpdateRates', e?.target?.checked)}
              className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
            />
          </label>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Update Frequency
            </label>
            <select
              value={currencySettings?.updateFrequency}
              onChange={(e) => handleSettingChange('updateFrequency', e?.target?.value)}
              disabled={!currencySettings?.autoUpdateRates}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {updateFrequencies?.map(freq => (
                <option key={freq?.value} value={freq?.value}>{freq?.label}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Rate Provider
            </label>
            <select
              value={currencySettings?.rateProvider}
              onChange={(e) => handleSettingChange('rateProvider', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {rateProviders?.map(provider => (
                <option key={provider?.value} value={provider?.value}>{provider?.label}</option>
              ))}
            </select>
          </div>
        </div>
      </div>
      {/* Display Format */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Display Format</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <label className="flex items-center justify-between cursor-pointer">
            <span className="text-sm font-medium text-foreground">Show Currency Symbol</span>
            <input
              type="checkbox"
              checked={currencySettings?.showCurrencySymbol}
              onChange={(e) => handleSettingChange('showCurrencySymbol', e?.target?.checked)}
              className="h-4 w-4 rounded border border-input bg-background text-primary focus:ring-2 focus:ring-ring focus:ring-offset-2"
            />
          </label>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Symbol Position
            </label>
            <select
              value={currencySettings?.currencyPosition}
              onChange={(e) => handleSettingChange('currencyPosition', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              <option value="before">Before ($100)</option>
              <option value="after">After (100$)</option>
            </select>
          </div>

          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Negative Format
            </label>
            <select
              value={currencySettings?.negativeFormat}
              onChange={(e) => handleSettingChange('negativeFormat', e?.target?.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {negativeFormats?.map(format => (
                <option key={format?.value} value={format?.value}>{format?.label}</option>
              ))}
            </select>
          </div>

          <Input
            label="Decimal Places"
            type="number"
            value={currencySettings?.decimalPlaces}
            onChange={(e) => handleSettingChange('decimalPlaces', parseInt(e?.target?.value))}
            min="0"
            max="6"
          />
        </div>

        <div className="mt-6 p-4 bg-muted/30 rounded-lg">
          <p className="text-sm font-medium text-foreground mb-2">Format Preview:</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Positive: </span>
              <span className="font-medium">$1,234.56</span>
            </div>
            <div>
              <span className="text-muted-foreground">Negative: </span>
              <span className="font-medium">($1,234.56)</span>
            </div>
            <div>
              <span className="text-muted-foreground">Zero: </span>
              <span className="font-medium">$0.00</span>
            </div>
            <div>
              <span className="text-muted-foreground">Large: </span>
              <span className="font-medium">$1,234,567.89</span>
            </div>
          </div>
        </div>
      </div>
      {/* Currency List */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Currencies</h3>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted/30">
              <tr>
                <th className="text-left p-3 text-sm font-medium text-foreground">Currency</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Code</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Symbol</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Exchange Rate</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Last Updated</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Status</th>
                <th className="text-left p-3 text-sm font-medium text-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {currencies?.map((currency) => (
                <tr key={currency?.id} className="border-t border-border hover:bg-muted/20">
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-foreground">{currency?.name}</span>
                      {currency?.isBase && (
                        <span className="px-2 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                          Base
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3">
                    <span className="font-mono text-sm font-medium">{currency?.code}</span>
                  </td>
                  <td className="p-3">
                    <span className="font-medium">{currency?.symbol}</span>
                  </td>
                  <td className="p-3">
                    {currency?.isBase ? (
                      <span className="text-muted-foreground">1.0000 (Base)</span>
                    ) : (
                      <Input
                        type="number"
                        value={currency?.exchangeRate}
                        onChange={(e) => updateExchangeRate(currency?.id, e?.target?.value)}
                        step="0.0001"
                        min="0"
                        className="w-24 text-sm"
                      />
                    )}
                  </td>
                  <td className="p-3">
                    <span className="text-sm text-muted-foreground">{currency?.lastUpdated}</span>
                  </td>
                  <td className="p-3">
                    <button
                      onClick={() => toggleCurrencyStatus(currency?.id)}
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        currency?.isActive 
                          ? 'bg-green-100 text-green-700' :'bg-red-100 text-red-700'
                      }`}
                    >
                      {currency?.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="p-3">
                    <div className="flex items-center space-x-2">
                      {!currency?.isBase && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setBaseCurrency(currency?.id)}
                          iconName="Star"
                        >
                          Set Base
                        </Button>
                      )}
                      {!currency?.isBase && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteCurrency(currency?.id)}
                          iconName="Trash2"
                        >
                          Delete
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* Exchange Rate History */}
      <div className="bg-surface border border-border rounded-lg p-6">
        <h3 className="text-lg font-medium text-foreground mb-6">Exchange Rate History</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 border border-border rounded-lg text-center">
            <Icon name="TrendingUp" size={24} className="mx-auto mb-2 text-green-500" />
            <h4 className="font-medium text-foreground mb-1">Today's High</h4>
            <p className="text-sm text-muted-foreground">EUR: 0.8567</p>
          </div>

          <div className="p-4 border border-border rounded-lg text-center">
            <Icon name="TrendingDown" size={24} className="mx-auto mb-2 text-red-500" />
            <h4 className="font-medium text-foreground mb-1">Today's Low</h4>
            <p className="text-sm text-muted-foreground">EUR: 0.8432</p>
          </div>

          <div className="p-4 border border-border rounded-lg text-center">
            <Icon name="BarChart3" size={24} className="mx-auto mb-2 text-primary" />
            <h4 className="font-medium text-foreground mb-1">Average</h4>
            <p className="text-sm text-muted-foreground">EUR: 0.8500</p>
          </div>
        </div>
      </div>
      {/* Add Currency Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-surface border border-border rounded-lg w-full max-w-md">
            <div className="flex items-center justify-between p-6 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">Add New Currency</h3>
              <Button variant="ghost" size="icon" onClick={() => setShowAddModal(false)}>
                <Icon name="X" size={16} />
              </Button>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="text-sm font-medium text-foreground mb-2 block">
                  Select Currency
                </label>
                <select
                  value={newCurrency?.code}
                  onChange={(e) => {
                    const selected = availableCurrencies?.find(c => c?.code === e?.target?.value);
                    if (selected) {
                      setNewCurrency({
                        code: selected?.code,
                        name: selected?.name,
                        symbol: selected?.symbol,
                        exchangeRate: 1.0
                      });
                    }
                  }}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <option value="">Select a currency</option>
                  {availableCurrencies?.filter(c => !currencies?.some(cur => cur?.code === c?.code))?.map(currency => (
                    <option key={currency?.code} value={currency?.code}>
                      {currency?.code} - {currency?.name}
                    </option>
                  ))}
                </select>
              </div>

              {newCurrency?.code && (
                <>
                  <Input
                    label="Currency Name"
                    value={newCurrency?.name}
                    onChange={(e) => setNewCurrency(prev => ({ ...prev, name: e?.target?.value }))}
                    required
                  />

                  <Input
                    label="Currency Symbol"
                    value={newCurrency?.symbol}
                    onChange={(e) => setNewCurrency(prev => ({ ...prev, symbol: e?.target?.value }))}
                    required
                  />

                  <Input
                    label="Initial Exchange Rate"
                    type="number"
                    value={newCurrency?.exchangeRate}
                    onChange={(e) => setNewCurrency(prev => ({ ...prev, exchangeRate: parseFloat(e?.target?.value) }))}
                    step="0.0001"
                    min="0"
                    description="Rate relative to base currency"
                    required
                  />
                </>
              )}
            </div>

            <div className="flex justify-end space-x-4 p-6 border-t border-border">
              <Button variant="outline" onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleAddCurrency}
                disabled={!newCurrency?.code || !newCurrency?.name}
                iconName="Plus"
              >
                Add Currency
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CurrencySettings;